import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Link2, FlaskConical } from "lucide-react";
import { fmtUSD, fmtNum } from "@/lib/format";

export const TradeStationPanel = () => {
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/broker/tradestation/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/futures/sync/latest")
      .then(({ data }) => setSnapshot(data && data.positions ? data : null))
      .catch(() => {});
  }, []);

  const sync = async (demo) => {
    setBusy(true);
    try {
      const { data } = await api.post("/futures/sync/tradestation", demo ? { demo: true } : {});
      setSnapshot(data);
      toast.success(`Synced ${data.positions.length} position${data.positions.length === 1 ? "" : "s"}${demo ? " (demo)" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.connection?.connected;
  return (
    <Card className="border border-gray-200 rounded-2xl p-6 space-y-5" data-testid="tradestation-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="font-display font-semibold text-lg">TradeStation position sync</p>
          <p className="text-xs text-gray-500 mt-1 max-w-lg" data-testid="ts-status-notice">
            {status?.notice || "Checking configuration…"} Synced positions are read-only — they never
            touch your paper cash or margin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => sync(false)}
            disabled={busy}
            data-testid="ts-sync-btn"
            className="rounded-full bg-brand text-white hover:bg-brand-deep h-9"
          >
            {connected ? <RefreshCw size={12} className="mr-2" /> : <Link2 size={12} className="mr-2" />}
            {busy ? "Syncing…" : "Sync positions"}
          </Button>
          <Button
            onClick={() => sync(true)}
            disabled={busy}
            variant="outline"
            data-testid="ts-demo-sync-btn"
            className="rounded-full h-9"
          >
            <FlaskConical size={12} className="mr-2" /> Demo sync
          </Button>
        </div>
      </div>

      {snapshot && (
        <div data-testid="ts-snapshot">
          <p className="text-xs text-gray-500">
            Last synced {new Date(snapshot.synced_at).toLocaleString()}
            {snapshot.demo && (
              <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]" data-testid="ts-demo-badge">
                DEMO DATA
              </Badge>
            )}
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4">Contract</th>
                  <th className="py-2 pr-4">Root</th>
                  <th className="py-2 pr-4">Direction</th>
                  <th className="py-2 pr-4">Qty</th>
                  <th className="py-2 pr-4">Avg price</th>
                  <th className="py-2">Market value</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.positions.map((p, i) => (
                  <tr key={i} className="border-b border-gray-50" data-testid={`ts-position-row-${i}`}>
                    <td className="py-2 pr-4 font-mono">{p.raw_symbol}</td>
                    <td className="py-2 pr-4">
                      {p.supported ? (
                        <span className="font-mono">{p.root}{p.expiration ? ` · ${p.expiration}` : ""}</span>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 text-[10px]">unsupported</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`text-[10px] uppercase font-bold ${p.direction === "long" ? "text-gain" : "text-loss"}`}>
                        {p.direction}
                      </span>
                    </td>
                    <td className="py-2 pr-4 font-mono">{fmtNum(p.quantity)}</td>
                    <td className="py-2 pr-4 font-mono">{fmtUSD(p.avg_price)}</td>
                    <td className="py-2 font-mono">{fmtUSD(p.market_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TradeStationPanel;
